import { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Card,
  Group,
  Grid,
  RingProgress,
  Timeline,
  Select,
  Button,
  ScrollArea,
  Badge,
  Box,
  Center,
  Loader,
  Alert,
} from '@mantine/core';
import {
  IconClock,
  IconActivity,
  IconRefresh,
  IconCalendar,
  IconChartBar,
  IconCircleFilled,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Text } from '../../components/ui';
import { FadeIn, SlideIn } from '../../components/animations';
import {
  usePresenceAnalyticsService,
  type UserPresenceStats,
  type PresenceTimelineEntry,
  type PresenceHeatmap,
} from '@actschurch/ccms-sdk';
import { IdleTimeDisplay } from '../../components/ui/IdleTimeDisplay';

type TimeRange = '7d' | '30d' | '90d' | 'custom';

export default function PresenceAnalyticsPage() {
  const presenceService = usePresenceAnalyticsService();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [stats, setStats] = useState<UserPresenceStats | null>(null);
  const [timeline, setTimeline] = useState<PresenceTimelineEntry[]>([]);
  const [, setHeatmap] = useState<PresenceHeatmap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getDateRange = (range: TimeRange): { startDate: string; endDate: string } => {
    const endDate = new Date();
    let startDate = new Date();

    switch (range) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        break;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange(timeRange);

      // Fetch stats, timeline, and heatmap in parallel using the SDK service
      const [statsRes, timelineRes, heatmapRes] = await Promise.all([
        presenceService.getMyStats(new Date(startDate), new Date(endDate)),
        presenceService.getMyTimeline(new Date(startDate), new Date(endDate), 50),
        presenceService.getMyHeatmap(new Date(startDate), new Date(endDate)),
      ]);

      setStats(statsRes);
      setTimeline(timelineRes);
      setHeatmap(heatmapRes);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'online':
        return 'green';
      case 'away':
        return 'yellow';
      case 'busy':
        return 'orange';
      case 'offline':
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    return <IconCircleFilled size={12} style={{ color: `var(--mantine-color-${getStatusColor(status)}-6)` }} />;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Center style={{ minHeight: 400 }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">Loading analytics...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} title="No Data" color="blue">
          No presence data available for the selected time range.
        </Alert>
      </Container>
    );
  }

  const totalHours = stats.totalOnlineHours + stats.totalAwayHours;
  // const onlinePercentage = totalHours > 0 ? (stats.totalOnlineHours / totalHours) * 100 : 0;
  const awayPercentage = totalHours > 0 ? (stats.totalAwayHours / totalHours) * 100 : 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <FadeIn>
          <Group justify="space-between">
            <div>
              <Text size="xl" fw={700}>Presence Analytics</Text>
              <Text size="sm" c="dimmed">View your presence activity and statistics</Text>
            </div>
            <Group gap="xs">
              <Select
                value={timeRange}
                onChange={(value) => setTimeRange(value as TimeRange)}
                data={[
                  { value: '7d', label: 'Last 7 days' },
                  { value: '30d', label: 'Last 30 days' },
                  { value: '90d', label: 'Last 90 days' },
                ]}
                leftSection={<IconCalendar size={16} />}
                style={{ width: 180 }}
              />
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                onClick={fetchAnalytics}
              >
                Refresh
              </Button>
            </Group>
          </Group>
        </FadeIn>

        {/* Summary Cards */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <SlideIn delay={0}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconClock size={20} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" c="dimmed">Total Online</Text>
                  </Group>
                  <Text size="xl" fw={700}>{stats.totalOnlineHours.toFixed(1)}h</Text>
                  <Text size="xs" c="dimmed">
                    {((stats.totalOnlineHours / (parseInt(timeRange) || 30)) / 24 * 100).toFixed(0)}% of time
                  </Text>
                </Stack>
              </Card>
            </SlideIn>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <SlideIn delay={100}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconActivity size={20} color="var(--mantine-color-yellow-6)" />
                    <Text size="sm" c="dimmed">Total Away</Text>
                  </Group>
                  <Text size="xl" fw={700}>{stats.totalAwayHours.toFixed(1)}h</Text>
                  <Text size="xs" c="dimmed">{awayPercentage.toFixed(0)}% of active time</Text>
                </Stack>
              </Card>
            </SlideIn>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <SlideIn delay={200}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconChartBar size={20} color="var(--mantine-color-green-6)" />
                    <Text size="sm" c="dimmed">Avg Session</Text>
                  </Group>
                  <Text size="xl" fw={700}>{stats.averageSessionDuration.toFixed(0)}m</Text>
                  <Text size="xs" c="dimmed">Average duration</Text>
                </Stack>
              </Card>
            </SlideIn>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <SlideIn delay={300}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconRefresh size={20} color="var(--mantine-color-violet-6)" />
                    <Text size="sm" c="dimmed">Status Changes</Text>
                  </Group>
                  <Text size="xl" fw={700}>{stats.totalStatusChanges}</Text>
                  <Text size="xs" c="dimmed">
                    {stats.manualChanges} manual, {stats.automaticChanges} auto
                  </Text>
                </Stack>
              </Card>
            </SlideIn>
          </Grid.Col>
        </Grid>

        <Grid>
          {/* Status Breakdown */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn delay={400}>
              <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
                <Stack gap="md">
                  <Text size="lg" fw={600}>Status Distribution</Text>
                  <Center>
                    <RingProgress
                      size={200}
                      thickness={24}
                      sections={Object.entries(stats.statusBreakdown).map(([status, count]) => ({
                        value: (count / stats.totalStatusChanges) * 100,
                        color: getStatusColor(status),
                        tooltip: `${status}: ${count} (${((count / stats.totalStatusChanges) * 100).toFixed(1)}%)`,
                      }))}
                      label={
                        <Center>
                          <Stack gap={0} align="center">
                            <Text size="xl" fw={700}>{totalHours.toFixed(1)}h</Text>
                            <Text size="xs" c="dimmed">Total Active</Text>
                          </Stack>
                        </Center>
                      }
                    />
                  </Center>
                  <Stack gap="xs">
                    {Object.entries(stats.statusBreakdown).map(([status, count]) => (
                      <Group key={status} justify="space-between">
                        <Group gap="xs">
                          {getStatusIcon(status)}
                          <Text size="sm">{status}</Text>
                        </Group>
                        <Badge color={getStatusColor(status)} variant="light">
                          {count} ({((count / stats.totalStatusChanges) * 100).toFixed(1)}%)
                        </Badge>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </FadeIn>
          </Grid.Col>

          {/* Platform Breakdown */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn delay={500}>
              <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
                <Stack gap="md">
                  <Text size="lg" fw={600}>Platform Usage</Text>
                  {Object.keys(stats.platformBreakdown).length > 0 ? (
                    <Stack gap="md">
                      {Object.entries(stats.platformBreakdown).map(([platform, count]) => {
                        const percentage = (count / stats.totalStatusChanges) * 100;
                        return (
                          <Box key={platform}>
                            <Group justify="space-between" mb="xs">
                              <Text size="sm" tt="capitalize">{platform}</Text>
                              <Text size="sm" fw={600}>{percentage.toFixed(1)}%</Text>
                            </Group>
                            <Box
                              style={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'var(--mantine-color-gray-2)',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                style={{
                                  height: '100%',
                                  width: `${percentage}%`,
                                  backgroundColor: 'var(--mantine-color-blue-6)',
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Center py="xl">
                      <Text size="sm" c="dimmed">No platform data available</Text>
                    </Center>
                  )}
                </Stack>
              </Card>
            </FadeIn>
          </Grid.Col>
        </Grid>

        {/* Activity Timeline */}
        <FadeIn delay={600}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text size="lg" fw={600}>Recent Activity</Text>
                <Badge variant="light">{timeline.length} events</Badge>
              </Group>
              {timeline.length > 0 ? (
                <ScrollArea h={400}>
                  <Timeline active={-1} bulletSize={24} lineWidth={2}>
                    {timeline.map((entry) => (
                      <Timeline.Item
                        key={entry.id}
                        bullet={getStatusIcon(entry.status)}
                        title={
                          <Group gap="xs">
                            <Text size="sm" fw={600}>{entry.status}</Text>
                            {entry.isAutomatic && (
                              <Badge size="xs" variant="light" color="gray">Auto</Badge>
                            )}
                            {entry.platform && (
                              <Badge size="xs" variant="light">{entry.platform}</Badge>
                            )}
                          </Group>
                        }
                      >
                        <Stack gap={4}>
                          {entry.activityMessage && (
                            <Text size="sm" c="dimmed">{entry.activityMessage}</Text>
                          )}
                          <Group gap="xs">
                            <IdleTimeDisplay lastSeen={entry.timestamp} size="xs" />
                            {entry.durationSeconds && (
                              <Text size="xs" c="dimmed">• Duration: {formatDuration(entry.durationSeconds)}</Text>
                            )}
                          </Group>
                        </Stack>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </ScrollArea>
              ) : (
                <Center py="xl">
                  <Text size="sm" c="dimmed">No recent activity</Text>
                </Center>
              )}
            </Stack>
          </Card>
        </FadeIn>
      </Stack>
    </Container>
  );
}
